<template>
  <div>
    <ListView
      v-if="viewMode === VIEW_MODES.list"
      :tasks="tasks"
      :title="titleWithCount"
      :isLoading="isLoading"
      :isInitialLoading="isInitialLoading"
      :hideOnEmpty="hideOnEmpty"
      :groupKey="groupKey"
      :shouldPaginate="shouldPaginate"
      @radioClick="handleRadioClick"
      @delete="handleDelete"
      @duplicate="duplicateTask"
      @viewMore="handlePageChange"
      @selectTask="handleSelectTask"
    />
    <TableView
      v-if="viewMode === VIEW_MODES.table"
      :tasks="tasks"
      :title="titleWithCount"
      :groupKey="groupKey"
      :isLoading="isLoading"
      :shouldPaginate="shouldPaginate"
      @radioClick="handleRadioClick"
      @delete="handleDelete"
      @duplicate="duplicateTask"
      @viewMore="handlePageChange"
      @selectTask="handleSelectTask"
    />
    <DeleteConfirmationModal
      :show="showDeleteDialog"
      :isLoading="isDeleting"
      :title="$t('tasks.delete_title')"
      :text="$t('tasks.delete_text')"
      @cancel="handleCancelDelete"
      @delete="deleteTask"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import mixin from '@/mixins/mixin.vue'
import _ from 'lodash'

import api from '@/api'
import getEntityId from '@/lib/getEntityId'
import access from '@/lib/access.js'

import TableView from '../TableView.vue'
import ListView from '../ListView.vue'

import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

const DEFAULT_LIMIT = 20
const VIEW_MODES = {
  table: 'table',
  list: 'list',
}

export default {
  name: 'TaskGroup',

  mixins: [mixin, trackerMixin],

  components: {
    TableView,
    DeleteConfirmationModal,
    ListView,
  },

  props: {
    groupFilters: {
      type: Object,
      default: () => {},
    },
    groupSort: {
      type: Object,
      default: () => {},
    },
    title: {
      type: String,
      default: () => '',
    },
    initialLimit: {
      type: Number,
      default: 0,
    },
    groupKey: {
      type: String,
      default: () => '',
    },
    viewMode: {
      type: String,
      default: () => VIEW_MODES.table,
    },
    hideOnEmpty: {
      type: Boolean,
      default: () => false,
    },
    autorefreshOnQueryChange: {
      type: Boolean,
      default: () => true,
    },
  },

  data() {
    return {
      tasks: [],
      totalRecords: null,
      page: 1,
      isLoading: false,
      isInitialLoading: true,
      showDeleteDialog: false,
      isDeleting: false,
      taskToDelete: '',
      VIEW_MODES,
    }
  },

  mounted() {
    this.fetchNextPage()
  },

  computed: {
    ...mapGetters(['organizationId', 'accountId', 'account']),

    userIsCoachOrAdmin() {
      return access.isLeadershipOrCoach(this.account)
    },

    titleWithCount() {
      return this.totalRecords > 0
        ? `${this.title} (${this.totalRecords})`
        : this.title
    },

    shouldPaginate() {
      return this.totalRecords > this.tasks.length
    },

    getCalculatedFrom() {
      if (this.page === 2) return this.initialLimit
      if (this.page > 2)
        return this.initialLimit + (this.page - 2) * DEFAULT_LIMIT
      return 0
    },

    filters() {
      const filters = {
        excludedAssignees:
          _.isArray(this.$route.query.excludedAssignees) ||
          !this.$route.query.excludedAssignees
            ? this.$route.query.excludedAssignees
            : [this.$route.query.excludedAssignees],
        assignees:
          _.isArray(this.$route.query.assignees) || !this.$route.query.assignees
            ? this.$route.query.assignees
            : [this.$route.query.assignees],
        status:
          _.isArray(this.$route.query.status) || !this.$route.query.status
            ? this.$route.query.status
            : [this.$route.query.status],
        site:
          _.isArray(this.$route.query.site) || !this.$route.query.site
            ? this.$route.query.site
            : [this.$route.query.site],
        createdBy: this.$route.query.createdBy,
      }

      return filters
    },

    sort() {
      if (this.$route?.query?.searchTerms?.length)
        return { by: '_score', order: 'desc' }

      if (!this.$route.query.sort) return this.groupSort

      const [sortBy, sortOrder] = this.$route.query.sort.split('-')

      return { by: sortBy ?? 'duedate', order: sortOrder ?? 'desc' }
    },

    shouldAutoAssign() {
      return Boolean(this.$route.query.type === 'assignedToMe')
    },
  },

  methods: {
    async refreshList() {
      this.isLoading = true
      setTimeout(async () => {
        const limit = this.initialLimit + DEFAULT_LIMIT * (this.page - 1)
        this.tasks = await this.fetchTasks({ limit, from: 0 })
      }, 1000) // this setTimeout is a temporal solution for a problem with the delay between saving data in stream and fetching data

      // Add event mixpanel to catch groupKey, limit and filters
    },

    async fetchNextPage() {
      const limit = this.page === 1 ? this.initialLimit : DEFAULT_LIMIT
      const tasks = await this.fetchTasks({
        limit,
        from: this.getCalculatedFrom,
      })
      this.tasks = [...this.tasks, ...(tasks ?? [])]
    },

    async fetchTasks({ limit, from }) {
      try {
        this.isLoading = true
        const { data } = await api.tasks.getAll({
          organizationId: this.organizationId,
          filters: {
            ...this.filters,
            ...this.groupFilters,
          },
          sort: this.sort,
          terms: this.$route.query.searchTerms,
          limit,
          from,
          projections: [
            'name',
            'assignees',
            'dueDate',
            'priority',
            'status',
            'description',
            'isPrivate',
            'createdBy',
            'media',
            'linkedEntityId',
            'linkedEntityType',
            'tags',
          ],
          expansions: ['assignees', 'createdBy', 'tags'],
        })
        this.totalRecords = data.totalRecordsInData ?? 0

        return data.results
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
        this.isInitialLoading = false
      }
    },

    async handleRadioClick(status, taskId, taskIndex) {
      try {
        this.isLoading = true
        const newStatus =
          status === this.$constants.tasksStatus.open ||
          status === this.$constants.tasksStatus.inProgress
            ? this.$constants.tasksStatus.completed
            : this.$constants.tasksStatus.open
        this.tasks[taskIndex].status = newStatus
        this.tasks = [...this.tasks]

        await api.tasks.update(this.organizationId, taskId, {
          status: newStatus,
        })

        this.showSuccess(
          newStatus === this.$constants.tasksStatus.completed
            ? this.$t('tasks.completed_success')
            : this.$t('tasks.reopen_success')
        )

        if (this.filters?.status?.length || this.groupFilters?.status?.length) {
          this.tasks = this.tasks.filter((task, index) => index !== taskIndex)
          this.totalRecords -= 1
        }

        this.trackTasks('tasks.complete', {
          taskId,
          previousStatus: status,
          status: newStatus,
          section: this.$route.name,
        })
        this.$emit('statusToggle')
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    handleDelete(taskId) {
      this.taskToDelete = taskId
      this.showDeleteDialog = true
    },

    handleCancelDelete() {
      this.taskToDelete = ''
      this.showDeleteDialog = false
    },

    async deleteTask() {
      try {
        this.isDeleting = true
        await api.tasks.delete(this.organizationId, this.taskToDelete)
        this.showSuccess(this.$t('tasks.deleted_successfully'))
        this.refreshList()
        this.trackTasks('tasks.delete', {
          taskId: this.taskToDelete,
          from: 'taskView',
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isDeleting = false
        this.showDeleteDialog = false
        this.taskToDelete = ''
      }
    },

    handlePageChange() {
      this.page += 1
      this.fetchNextPage()
    },

    async fetchAttachmentsById(taskId) {
      try {
        const { data } = await api.tasks.getAttachments(
          this.organizationId,
          taskId
        )
        return data?.map?.(attachment => {
          return attachment?.media?.id
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async updateAttachments(attachmentsIds, taskId) {
      attachmentsIds?.forEach(id => {
        api.tasks.attach(this.organizationId, taskId, { media: id })
      })
    },

    async duplicateTask(taskToDuplicate) {
      try {
        this.isLoading = true
        const attachmentsIds = await this.fetchAttachmentsById(
          taskToDuplicate?.id
        )

        const tags = taskToDuplicate?.tags?.map?.(getEntityId) ?? []
        const site = taskToDuplicate?.assignees?.[0]?.site ?? this.account?.site
        const task = _.omit(
          {
            ..._.omitBy(taskToDuplicate, _.isNull),
            assignees: this.shouldAutoAssign ? [this.accountId] : [],
            status: this.$constants.tasksStatus.open,
            tags: tags,
            site: getEntityId(site),
            name: `${taskToDuplicate.name} ${this.$t('boards.copy')}`,
            media: taskToDuplicate?.media ?? [],
            entityId: taskToDuplicate.linkedEntityId,
            entityType: taskToDuplicate.linkedEntityType,
          },
          'id'
        )
        const { data } = await api.tasks.create(this.organizationId, task)
        if (!_.isEmpty(attachmentsIds)) {
          this.updateAttachments(attachmentsIds, data.id)
        }
        this.showSuccess(this.$t('tasks.duplicate_success'))
        this.refreshList()
        this.trackTasks('tasks.duplicate', {
          from: 'taskView',
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    resetPagination() {
      this.tasks = []
      this.totalRecords = 0
      this.page = 1
    },

    resetInitialLoading() {
      this.isInitialLoading = true
    },

    handleSelectTask(taskId) {
      const savedScrollY = window.scrollY
      this.replaceRoute({
        path: this.$route.fullPath,
        query: { ...this.$route.query, task: taskId, groupKey: this.groupKey },
      })
      this.$nextTick(() => {
        window.scrollTo(0, savedScrollY)
      })
      this.$emit('selectTask', taskId)
    },
  },

  watch: {
    '$route.query'() {
      if (!this.autorefreshOnQueryChange) return
      this.resetPagination()
      this.fetchNextPage()
    },
  },
}
</script>
